import { useState, useEffect } from "react";
import { authFetch } from "../api";
import CurrencyInput from "./CurrencyInput";
import ListingDebugModal from "./ListingDebugModal";

const BEDROOM_OPTIONS = [null, 0, 1, 2, 3, 4, 5];
const BATHROOM_OPTIONS = [null, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5];
const WASHER_DRYER_OPTIONS = [
  { value: null, label: "-" },
  { value: "Y", label: "Yes" },
  { value: "N", label: "No" },
  { value: "U", label: "Unknown" },
];
const RENOVATION_OPTIONS = [
  { value: null, label: "-" },
  { value: "1", label: "1 - Low" },
  { value: "2", label: "2 - Mid-low" },
  { value: "3", label: "3 - Mid" },
  { value: "4", label: "4 - Mid-high" },
  { value: "5", label: "5 - High" },
];
const VIEWS_OPTIONS = [
  { value: null, label: "-" },
  { value: "0", label: "No" },
  { value: "1", label: "Premium" },
];
const OUTDOOR_TYPE_OPTIONS = [
  { value: "", label: "No" },
  { value: "juliet", label: "Juliet Balcony" },
  { value: "balcony", label: "Balcony" },
  { value: "terrace", label: "Terrace" },
  { value: "garden", label: "Garden/Yard" },
  { value: "roof_deck", label: "Roof Deck" },
  { value: "unknown", label: "Unknown Type" },
];
const STATUS_OPTIONS = [
  "ACTIVE", "APP ACCEPTED", "APP IN", "APP IN THRU US",
  "BACK ON MKT", "BOARD APPROVED", "CLOSED", "CLOSED THRU US",
  "COMING SOON", "CONTRACT OUT", "CONTRACT SIGNED",
  "LSE OUT", "LSE SIGN", "OFF EXCLUSIVE", "OFFER ACCEPTED",
  "PERM OFF MKT", "RENTED", "RENTED THRU US", "TEMP OFF MKT",
  "DELETE", "DELETED", "DELISTED", "FUTURE", "NOT RENTED", "SOLD",
];
const MEDIA_STATUS_OPTIONS = [
  { value: "", label: "-" },
  { value: "available", label: "Available" },
  { value: "not_available", label: "Not Available" },
];
const SQFT_SOURCE_OPTIONS = [
  { value: "", label: "-" },
  { value: "floor_plan", label: "Floor Plan" },
  { value: "dob_filings", label: "DOB Filings" },
  { value: "listing_description", label: "Listing Description" },
  { value: "broker", label: "Broker" },
  { value: "streeteasy", label: "StreetEasy" },
  { value: "other", label: "Other" },
];

// Verify-gate reason codes -> plain-English text for the Auto Verify message.
const VERIFY_REASON_TEXT = {
  floor_missing: "couldn't determine the floor — set it manually",
  floor_line_missing: "couldn't determine the floor line",
  sqft_missing: "square footage is missing",
  reno_high_no_dw: "highly renovated but no dishwasher — confirm the dishwasher",
  reno_no_description: "no description to gauge renovation — set the reno level",
  beds_baths_mismatch: "beds/baths don't match StreetEasy — reconcile them",
  hoa_missing: "HOA / common charges missing",
  tax_missing: "tax amount missing",
  whole_building_unit: "whole-building or unusual unit — review manually",
  no_se_source: "no StreetEasy listing found to enrich from",
  se_url: "a StreetEasy URL to scrape",
};
const verifyReason = (code) => VERIFY_REASON_TEXT[code] || (code || "").replace(/_/g, " ");

export default function ListingEditModal({ listing: listingProp, onClose, onSave, onVerify }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  // Auto Verify (enqueues a full re-enrich: SE scrape -> tax -> AI reno/DW -> gate)
  const [autoVerifying, setAutoVerifying] = useState(false);
  const [autoVerifyMsg, setAutoVerifyMsg] = useState(null);
  // Change listing type (sale <-> rental) — rare data-correction, with a warning
  const [showTypeChange, setShowTypeChange] = useState(false);
  const [changingType, setChangingType] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  // History
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showAddHistory, setShowAddHistory] = useState(false);
  const [newHistory, setNewHistory] = useState({ list_price: "", listing_status: "", changed_date: "", event_name: "" });
  const [addingHistory, setAddingHistory] = useState(false);
  const [checked, setChecked] = useState({});
  const toggleChecked = (key) => setChecked((c) => ({ ...c, [key]: !c[key] }));
  // SE paste
  const [showSEPaste, setShowSEPaste] = useState(false);
  const [sePasteText, setSePasteText] = useState("");
  const [seParsed, setSeParsed] = useState(null);
  const [seAdding, setSeAdding] = useState(false);

  const [fetchedListing, setFetchedListing] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(false);

  // If listing only has id (no full data), fetch it
  useEffect(() => {
    // New listing selected → drop any cached fetch from a previous listing so it can't
    // leak across (open A, close, open B was showing A's data).
    setFetchedListing(null);
    if (!listingProp || listingProp.bedrooms !== undefined) return; // already has full data
    setFetchLoading(true);
    authFetch(`/listings/${listingProp.id}/detail`)
      .then((data) => {
        setFetchedListing({ ...listingProp, ...data });
        setFetchLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch listing detail:", err);
        setFetchLoading(false);
      });
  }, [listingProp?.id]);   // key on id, not object ref, so an auto-verify refresh isn't wiped

  // Only trust the fetched copy when it belongs to the listing currently open.
  const effectiveListing =
    fetchedListing && fetchedListing.id === listingProp?.id ? fetchedListing : listingProp;

  useEffect(() => {
    if (!effectiveListing || (effectiveListing.bedrooms === undefined && !fetchedListing)) return;
    const ld = effectiveListing.listing_details || {};
    // Restore checked sections from listing_details
    setChecked(ld.sections_checked || {});
    // SqFt: show verified from listing_details or total_sqft (non-estimated), estimated separately
    const el = effectiveListing;
    const sqftVerified = ld.sqft_verified || (el.sqft && !el.sqft_is_estimated ? el.sqft : "") || "";
    const sqftEstimated = el.estimated_sqft || (el.sqft_is_estimated ? el.sqft : "") || "";

    // Auto-detect floor plan & video from media table
    const fpLink = ld.floorplan_link || el.floorplan_url_from_media || "";
    const fpStatus = fpLink ? "available" : (ld.floorplan_status || "");
    const vidLink = ld.video_link || el.video_url_from_media || "";
    const vidStatus = vidLink ? "available" : (ld.video_status || "");

    setForm({
      // Existing columns
      unit_number: el.unit_number || "",
      floor: el.floor || "",
      bedrooms: el.bedrooms,
      bathrooms: el.bathrooms,
      sqft: sqftEstimated,
      list_price: el.list_price,
      net_effective_rent: el.net_effective_rent || "",
      months_free: el.months_free || "",
      lease_term_months: el.lease_term_months || "",
      listing_status: el.listing_status,
      created_date: el.created_date ? el.created_date.split("T")[0] : "",
      closing_date: el.closing_date ? el.closing_date.split("T")[0] : "",
      dishwasher: el.dishwasher,
      washer_dryer: el.washer_dryer,
      private_outdoor_space: el.private_outdoor_space,
      outdoor_type: el.outdoor_type || (el.private_outdoor_space ? "unknown" : ""),
      renovation_level: el.renovation_level,
      premium_views: el.premium_views,
      hoa: el.hoa,
      tax: el.tax,
      tax_abatement: el.tax_abatement || false,
      tax_abatement_description: el.tax_abatement_description || "",
      furnished: el.furnished || false,
      floor_line: el.floor_line || "",
      listing_url: el.listing_url || "",
      block: el.block || "",
      lot: el.lot || "",
      borough: el.borough || "",
      acris_url: el.acris_url || "",
      is_commercial: el.ownership_type === "PROF",
      deeded_parking: el.deeded_parking || false,
      // listing_details fields
      sqft_verified: sqftVerified,
      sqft_verified_source: ld.sqft_verified_source || "",
      sqft_verified_source_other: ld.sqft_verified_source_other || "",
      sqft_exterior: ld.sqft_exterior || "",
      sqft_exterior_source: ld.sqft_exterior_source || "",
      sqft_exterior_source_other: ld.sqft_exterior_source_other || "",
      floorplan_status: fpStatus,
      floorplan_link: fpLink,
      floorplan_notes: ld.floorplan_notes || "",
      video_status: vidStatus,
      video_link: vidLink,
      video_notes: ld.video_notes || "",
      // Same definition as the backend (predictions.py / audit_verification.py) and the
      // Analysis checklist: a successful tax extraction confirms taxes, not just an acris link.
      taxes_confirmed: !!(
        el.acris_url ||
        el.tax_extracted_at ||
        ld.tax_bill?.status === "success" ||
        ld.tax_bill?.status === "no_data_verified"
      ),
      tax_abatement_verified_date: ld.tax_abatement_verified_date || "",
      tax_abatement_type: ld.tax_abatement_type || "",
      tax_abatement_type_other: ld.tax_abatement_type_other || "",
      tax_abatement_with: ld.tax_abatement_with || "",
      tax_abatement_without: ld.tax_abatement_without || "",
      comments: ld.comments || "",
    });
    setHistory([]);
    setShowHistory(false);
    setShowAddHistory(false);
  }, [effectiveListing, fetchedListing]);

  if (!listingProp) return null;
  if (fetchLoading) return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 9998, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", padding: 32, borderRadius: 8, fontSize: 14 }}>Loading listing...</div>
    </div>
  );

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const loadHistory = async () => {
    try {
      const data = await authFetch(`/listings/${listing.id}/history`);
      setHistory(data.history || []);
      setShowHistory(true);
    } catch (error) {
      console.error("Error loading history:", error);
    }
  };

  const addHistoryEntry = async () => {
    if (!newHistory.changed_date) return;
    setAddingHistory(true);
    try {
      await authFetch(`/listings/${listing.id}/history`, {
        method: "POST",
        body: JSON.stringify({
          list_price: newHistory.list_price ? parseFloat(newHistory.list_price) : null,
          listing_status: newHistory.listing_status || null,
          changed_date: newHistory.changed_date,
          event_name: newHistory.event_name || null,
        }),
      });
      setNewHistory({ list_price: "", listing_status: "", changed_date: "", event_name: "" });
      setShowAddHistory(false);
      loadHistory();
    } catch (error) {
      console.error("Error adding history:", error);
      alert("Failed to add history entry");
    } finally {
      setAddingHistory(false);
    }
  };

  const SE_EVENT_MAP = {
    "listed for sale": "ACTIVE",
    "listed for rent": "ACTIVE",
    "listed by": "ACTIVE",
    "back on market": "ACTIVE",
    "relisted": "ACTIVE",
    "price increased": "ACTIVE",
    "price decreased": "ACTIVE",
    "temporarily off market": "TEMP OFF MKT",
    "delisted": "TEMP OFF MKT",
    "removed": "TEMP OFF MKT",
    "no longer available": "PERM OFF MKT",
    "listed": "ACTIVE",
    "entered contract": "CONTRACT SIGNED",
    "in contract": "CONTRACT SIGNED",
    "contract signed": "CONTRACT SIGNED",
    "sale closed": "CLOSED",
    "closing recorded": "CLOSED",
    "sold": "CLOSED",
    "rented by": "RENTED",
    "rented": "RENTED",
  };

  const parseSEHistory = (text) => {
    const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
    // Skip header line if present
    const start = /^date\s/i.test(lines[0]) ? 1 : 0;
    const entries = [];
    let i = start;
    while (i < lines.length) {
      // Look for a date line: M/D/YYYY
      const dateMatch = lines[i].match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (!dateMatch) { i++; continue; }
      const [, month, day, year] = dateMatch;
      const changed_date = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
      i++;
      // Next line: price
      let list_price = null;
      if (i < lines.length) {
        const priceMatch = lines[i].match(/\$([\d,]+)/);
        if (priceMatch) { list_price = parseInt(priceMatch[1].replace(/,/g, "")); i++; }
      }
      // Next line: event
      let event = "";
      if (i < lines.length && !/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(lines[i]) && !/^closing record$/i.test(lines[i])) {
        event = lines[i]; i++;
      }
      // Skip "Closing record" if present
      if (i < lines.length && /^closing record$/i.test(lines[i])) i++;
      // Map event to status
      const eventLower = event.toLowerCase();
      let listing_status = "";
      for (const [key, status] of Object.entries(SE_EVENT_MAP)) {
        if (eventLower.startsWith(key) || eventLower.includes(" " + key)) { listing_status = status; break; }
      }
      entries.push({ changed_date, list_price, event, listing_status, include: true });
    }
    return entries;
  };

  const handleSEParse = () => {
    const entries = parseSEHistory(sePasteText);
    if (entries.length === 0) { alert("Could not parse any history entries"); return; }
    // Mark dupes based on date + price + status match against existing history
    const existingKeys = new Set(history.map((h) => {
      const d = h.changed_date ? h.changed_date.split("T")[0] : "";
      const p = h.list_price || "";
      const s = h.listing_status || "";
      return `${d}|${p}|${s}`;
    }));
    const marked = entries.map((e) => {
      const key = `${e.changed_date}|${e.list_price || ""}|${e.listing_status || ""}`;
      const isDupe = existingKeys.has(key);
      return { ...e, include: !isDupe, isDupe };
    });
    setSeParsed(marked);
  };

  const toggleSEEntry = (idx) => {
    setSeParsed((prev) => prev.map((e, i) => i === idx ? { ...e, include: !e.include } : e));
  };

  const addSEEntries = async () => {
    const toAdd = seParsed.filter((e) => e.include);
    if (toAdd.length === 0) return;
    setSeAdding(true);
    try {
      for (const entry of toAdd) {
        await authFetch(`/listings/${listing.id}/history`, {
          method: "POST",
          body: JSON.stringify({
            list_price: entry.list_price,
            listing_status: entry.listing_status || null,
            changed_date: entry.changed_date,
            event_name: entry.event || null,
          }),
        });
      }
      setSePasteText("");
      setSeParsed(null);
      setShowSEPaste(false);
      loadHistory();
    } catch (error) {
      console.error("Error adding SE history:", error);
      alert("Failed to add some entries");
    } finally {
      setSeAdding(false);
    }
  };

  // Use effectiveListing (fetched or passed) everywhere below
  const listing = effectiveListing;

  const buildPayload = () => {
    const payload = {};
    const cmp = (a, b) => (a ?? "") === (b ?? "");

    // Regular columns
    if (!cmp(form.unit_number, listing.unit_number)) payload.unit_number = form.unit_number || null;
    if (!cmp(form.floor, listing.floor)) payload.floor = form.floor || null;
    if (form.bedrooms !== listing.bedrooms) payload.bedrooms = form.bedrooms;
    if (form.bathrooms !== listing.bathrooms) payload.bathrooms = form.bathrooms;
    if (form.sqft !== listing.sqft) payload.sqft = form.sqft ? parseInt(form.sqft) : null;
    if (form.list_price !== listing.list_price) payload.list_price = form.list_price ? parseFloat(form.list_price) : null;
    if (String(form.net_effective_rent || "") !== String(listing.net_effective_rent || "")) payload.net_effective_rent = form.net_effective_rent ? parseFloat(form.net_effective_rent) : null;
    if (String(form.months_free || "") !== String(listing.months_free || "")) payload.months_free = form.months_free ? parseFloat(form.months_free) : null;
    if (String(form.lease_term_months || "") !== String(listing.lease_term_months || "")) payload.lease_term_months = form.lease_term_months ? parseInt(form.lease_term_months) : null;
    if (form.listing_status !== listing.listing_status) payload.listing_status = form.listing_status;
    const origDate = listing.created_date ? listing.created_date.split("T")[0] : "";
    if (form.created_date !== origDate) payload.created_date = form.created_date || null;
    const origClosingDate = listing.closing_date ? listing.closing_date.split("T")[0] : "";
    if (form.closing_date !== origClosingDate) payload.closing_date = form.closing_date || null;
    if (form.active_dom != null && form.active_dom !== listing.active_dom) payload.active_dom = parseInt(form.active_dom);
    if (form.dishwasher !== listing.dishwasher) payload.dishwasher = form.dishwasher;
    if (form.washer_dryer !== listing.washer_dryer) payload.washer_dryer = form.washer_dryer;
    if (form.outdoor_type !== (listing.outdoor_type || (listing.private_outdoor_space ? "unknown" : ""))) payload.outdoor_type = form.outdoor_type || "none";
    if (form.renovation_level !== listing.renovation_level) payload.renovation_level = form.renovation_level;
    if (form.premium_views !== listing.premium_views) payload.premium_views = form.premium_views;
    if (form.hoa !== listing.hoa) payload.hoa = form.hoa ? parseFloat(form.hoa) : null;
    if (form.tax !== listing.tax) payload.tax = form.tax ? parseFloat(form.tax) : null;
    if (form.tax_abatement !== (listing.tax_abatement || false)) payload.tax_abatement = form.tax_abatement;
    if (!cmp(form.tax_abatement_description, listing.tax_abatement_description)) payload.tax_abatement_description = form.tax_abatement_description || null;
    if (form.furnished !== (listing.furnished || false)) payload.furnished = form.furnished;
    if (form.deeded_parking !== (listing.deeded_parking || false)) payload.deeded_parking = form.deeded_parking;
    if (!cmp(form.floor_line, listing.floor_line)) payload.floor_line = form.floor_line || null;
    if (!cmp(form.listing_url, listing.listing_url)) payload.listing_url = form.listing_url || null;
    if (!cmp(form.block, listing.block)) payload.block = form.block || null;
    if (!cmp(form.lot, listing.lot)) payload.lot = form.lot || null;
    if (!cmp(form.borough, listing.borough)) payload.borough = form.borough || null;
    if (!cmp(form.acris_url, listing.acris_url)) payload.acris_url = form.acris_url || null;
    // Commercial unit: send ownership_type change (use empty string to clear, since null is skipped by exclude_none)
    const wasCommercial = listing.ownership_type === "PROF";
    if (form.is_commercial !== wasCommercial) {
      payload.ownership_type = form.is_commercial ? "PROF" : "";
    }

    // listing_details JSON (only changed fields)
    const ld = listing.listing_details || {};
    const details = {};
    if (String(form.sqft_verified || "") !== String(ld.sqft_verified || "")) details.sqft_verified = form.sqft_verified ? parseInt(form.sqft_verified) : null;
    if ((form.sqft_verified_source || "") !== (ld.sqft_verified_source || "")) details.sqft_verified_source = form.sqft_verified_source || null;
    if ((form.sqft_verified_source_other || "") !== (ld.sqft_verified_source_other || "")) details.sqft_verified_source_other = form.sqft_verified_source_other || null;
    if (String(form.sqft_exterior || "") !== String(ld.sqft_exterior || "")) details.sqft_exterior = form.sqft_exterior ? parseInt(form.sqft_exterior) : null;
    if ((form.sqft_exterior_source || "") !== (ld.sqft_exterior_source || "")) details.sqft_exterior_source = form.sqft_exterior_source || null;
    if ((form.sqft_exterior_source_other || "") !== (ld.sqft_exterior_source_other || "")) details.sqft_exterior_source_other = form.sqft_exterior_source_other || null;
    if ((form.floorplan_status || "") !== (ld.floorplan_status || "")) details.floorplan_status = form.floorplan_status || null;
    if ((form.floorplan_link || "") !== (ld.floorplan_link || "")) details.floorplan_link = form.floorplan_link || null;
    if ((form.floorplan_notes || "") !== (ld.floorplan_notes || "")) details.floorplan_notes = form.floorplan_notes || null;
    if ((form.video_status || "") !== (ld.video_status || "")) details.video_status = form.video_status || null;
    if ((form.video_link || "") !== (ld.video_link || "")) details.video_link = form.video_link || null;
    if ((form.video_notes || "") !== (ld.video_notes || "")) details.video_notes = form.video_notes || null;
    const taxesConfirmed = !!form.acris_url;
    if (taxesConfirmed !== (ld.taxes_confirmed || false)) details.taxes_confirmed = taxesConfirmed;
    if ((form.tax_abatement_verified_date || "") !== (ld.tax_abatement_verified_date || "")) details.tax_abatement_verified_date = form.tax_abatement_verified_date || null;
    if ((form.tax_abatement_type || "") !== (ld.tax_abatement_type || "")) details.tax_abatement_type = form.tax_abatement_type || null;
    if ((form.tax_abatement_type_other || "") !== (ld.tax_abatement_type_other || "")) details.tax_abatement_type_other = form.tax_abatement_type_other || null;
    if (String(form.tax_abatement_with || "") !== String(ld.tax_abatement_with || "")) details.tax_abatement_with = form.tax_abatement_with ? parseFloat(form.tax_abatement_with) : null;
    if (String(form.tax_abatement_without || "") !== String(ld.tax_abatement_without || "")) details.tax_abatement_without = form.tax_abatement_without ? parseFloat(form.tax_abatement_without) : null;
    if ((form.comments || "") !== (ld.comments || "")) details.comments = form.comments || null;
    // Tax no data flag
    if (form.tax_no_data && ld.tax_bill?.status !== "no_data_verified") {
      details.tax_bill = { status: "no_data_verified", message: "No tax bills on DOF - verified by DQ team" };
    } else if (!form.tax_no_data && ld.tax_bill?.status === "no_data_verified") {
      details.tax_bill = { status: null };
    }
    // Save section checkboxes
    if (JSON.stringify(checked) !== JSON.stringify(ld.sections_checked || {})) details.sections_checked = checked;

    if (Object.keys(details).length > 0) payload.listing_details = details;
    return payload;
  };

  const handleSave = async () => {
    const payload = buildPayload();
    if (Object.keys(payload).length === 0) { onClose(); return; }
    setSaving(true);
    try {
      await authFetch(`/listings/${listing.id}`, { method: "PATCH", body: JSON.stringify(payload) });
      onSave(listing.id, form, payload.listing_details);
    } catch (error) {
      console.error("Error saving listing:", error);
      alert("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    const payload = buildPayload();
    setVerifying(true);
    try {
      if (Object.keys(payload).length > 0) {
        await authFetch(`/listings/${listing.id}`, { method: "PATCH", body: JSON.stringify(payload) });
      }
      await authFetch(`/listings/data-quality/${listing.id}/status?status=VERIFIED`, { method: "PATCH" });
      onVerify(listing.id);
    } catch (error) {
      console.error("Error verifying:", error);
      alert("Failed to verify listing");
    } finally {
      setVerifying(false);
    }
  };

  // Auto Verify: synchronous full re-enrich (SE scrape -> tax (sales) -> AI reno/DW -> gate).
  // The app API proxies to the verify-worker on the cron host; one await, ~15-30s.
  const handleAutoVerify = async () => {
    setAutoVerifying(true);
    setAutoVerifyMsg("Scraping & verifying…");
    try {
      const r = await authFetch(`/listings/${listing.id}/auto-verify`, { method: "POST" });
      if (r?.detail) {
        setAutoVerifyMsg(`Failed — ${r.detail}`);
      } else if (r?.skipped) {
        const SKIP = { ineligible_or_gone: "listing is closed, deleted, or no longer eligible" };
        setAutoVerifyMsg(`Skipped — ${SKIP[r.skipped] || String(r.skipped).replace(/_/g, " ")}`);
      } else {
        const g = r?.gate;
        let verdict;
        if (g === "VERIFIED") {
          const conf = r?.confidence ? ` (${r.confidence} confidence)` : "";
          const assumed = (r?.assumed && r.assumed.length) ? ` — assumed ${r.assumed.join(", ")}` : "";
          verdict = `✓ Verified${conf}${assumed}`;
        } else if (g === "PASS_TO_HUMAN") {
          const why = (r?.reasons || []).map(verifyReason).join("; ");
          verdict = why ? `Needs your review — ${why}` : "Needs your review";
        } else if (g === "WAITING") {
          verdict = `Waiting on ${r?.waiting_on ? verifyReason(r.waiting_on) : "more data"}`;
        } else {
          verdict = g || "Done";
        }
        setAutoVerifyMsg(verdict);
        // The worker scraped + (maybe) verified server-side, so the banner and amenity
        // fields are now stale. Re-pull the detail so the modal reflects the real state
        // (verified banner, filled DW/WD/outdoor/reno, etc.). Best-effort: keep the
        // verdict text even if the refresh fails.
        try {
          const fresh = await authFetch(`/listings/${listing.id}/detail`);
          setFetchedListing((prev) => ({ ...(prev || listingProp), ...fresh }));
        } catch (e) {
          console.error("Auto verify refresh failed:", e);
        }
      }
    } catch (error) {
      console.error("Auto verify error:", error);
      setAutoVerifyMsg("Failed to start");
    } finally {
      setAutoVerifying(false);
    }
  };

  const isSale = listing.sale_or_rental === "S";

  const handleChangeType = async () => {
    setChangingType(true);
    try {
      const r = await authFetch(`/listings/${listing.id}/change-type`, {
        method: "POST",
        body: JSON.stringify({ sale_or_rental: isSale ? "R" : "S" }),
      });
      if (r?.detail) { alert(`Failed: ${r.detail}`); return; }
      setShowTypeChange(false);
      // Keep the modal OPEN. Calling onSave/onClose here closed it and dropped the row from the
      // filtered results (a sale list no longer contains a freshly-made rental). Instead, refresh
      // the modal's own data so it reflects the new type (header label, type-specific fields, the
      // Make Sale/Make Rental button), and show a short confirmation.
      try {
        const fresh = await authFetch(`/listings/${listing.id}/detail`);
        setFetchedListing((prev) => ({ ...(prev || listingProp), ...fresh }));
      } catch (e) {
        console.error("change-type refresh failed:", e);
      }
      setAutoVerifyMsg(`✓ Now a ${isSale ? "rental" : "sale"} — reset to review; re-enter price, then re-verify`);
    } catch (e) {
      console.error("Change type failed:", e);
      alert("Failed to change listing type");
    } finally {
      setChangingType(false);
    }
  };
  const compositeSquareFeet = form.sqft_verified
    ? Math.round((parseInt(form.sqft_verified) || 0) + ((parseInt(form.sqft_exterior) || 0) / 3))
    : null;
  const daysOnMarket = listing.active_dom != null
    ? listing.active_dom
    : form.created_date
      ? Math.max(0, Math.floor(((listing.closing_date ? new Date(listing.closing_date) : new Date()) - new Date(form.created_date)) / (1000 * 60 * 60 * 24)))
      : null;

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div>
            <div style={{ fontSize: "16px", fontWeight: 700 }}>
              {listing.address}{listing.unit_number ? ` #${listing.unit_number}` : ""}
            </div>
            <div style={{ fontSize: "12px", color: "#666", marginTop: "2px", display: "flex", alignItems: "center", gap: "6px" }}>
              <span>ID: {listing.id} &bull; {listing.sale_or_rental === "S" ? "Sale" : "Rental"} &bull; {listing.condo_coop}
              {listing.building_tier ? ` \u2022 ${listing.building_tier}` : ""}
              {listing.neighborhood ? ` \u2022 ${listing.neighborhood}` : ""}</span>
              <button onClick={() => setShowTypeChange(true)} title="Change listing type (fix a mislabeled sale/rental)"
                style={{ marginLeft: "6px", fontSize: "10px", padding: "1px 7px", border: "1px solid #bbb", borderRadius: "10px", background: "#fff", cursor: "pointer", color: "#666", whiteSpace: "nowrap" }}>
                &#8646; {isSale ? "Make Rental" : "Make Sale"}
              </button>
              {listing.property_id && (
                <a href={`/buildings/${listing.property_id}/edit`} target="_blank" rel="noopener noreferrer"
                   style={{ marginLeft: "6px", fontSize: "14px", textDecoration: "none", opacity: 0.6 }}
                   title="Open building">&#127963;</a>
              )}
              <button onClick={() => setShowDebug(true)} title="Debug: provenance, RP presence, SE status, queue history"
                style={{ marginLeft: "6px", fontSize: "10px", padding: "1px 7px", border: "1px solid #bbb", borderRadius: "10px", background: "#fff", cursor: "pointer", color: "#666", whiteSpace: "nowrap" }}>
                &#128027; Debug
              </button>
              {isSale && (
                <label style={{ display: "flex", alignItems: "center", gap: "3px", marginLeft: "8px", cursor: "pointer", color: form.is_commercial ? "#856404" : "#999" }}>
                  <input type="checkbox" checked={form.is_commercial || false} onChange={(e) => update("is_commercial", e.target.checked)} style={{ width: "12px", height: "12px" }} />
                  <span style={{ fontSize: "11px", fontWeight: form.is_commercial ? 600 : 400 }}>Commercial</span>
                </label>
              )}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {autoVerifyMsg && (
              <span style={{ fontSize: "11px", maxWidth: "340px",
                color: autoVerifyMsg.startsWith("✓") ? "#2e7d32"
                  : (autoVerifyMsg.startsWith("Failed") || autoVerifyMsg.startsWith("Skipped")) ? "#c62828"
                  : "#e65100" }}>
                {autoVerifyMsg}
              </span>
            )}
            <button onClick={handleAutoVerify} disabled={autoVerifying} style={autoVerifyBtnStyle}
                    title="Re-scrape SE, refresh tax (sales), then run the verify gate">
              {autoVerifying ? "Auto Verifying…" : "Auto Verify"}
            </button>
            <button onClick={onClose} style={closeBtnStyle}>&times;</button>
          </div>
        </div>

        {/* Verified Status Bar */}
        <div style={{ padding: "8px 16px", backgroundColor: listing.verified ? "#e8f5e9" : "#fff3e0", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e0e0e0" }}>
          <div style={{ fontSize: "12px" }}>
            {listing.verified ? (
              <span style={{ color: "#2e7d32", fontWeight: 600 }}>
                Verified {listing.verified_by_name ? `by ${listing.verified_by_name}` : ""}{listing.verified_at ? ` on ${new Date(listing.verified_at).toLocaleDateString()}` : ""}
              </span>
            ) : (
              <span style={{ color: "#e65100" }}>Not verified</span>
            )}
          </div>
        </div>

        {/* Body */}
        <div style={bodyStyle}>
          {/* 1. Unit Info */}
          <Section title="Unit Info" sectionKey="unit_info" isChecked={checked.unit_info} onToggle={() => toggleChecked("unit_info")}>
            <FieldRow>
              <Field label="Unit" width="80px">
                <input type="text" value={form.unit_number || ""} onChange={(e) => update("unit_number", e.target.value)} style={inputStyle} />
              </Field>
              <Field label="Floor" width="60px">
                <input type="text" value={form.floor || ""} onChange={(e) => update("floor", e.target.value)} style={inputStyle} />
              </Field>
              <Field label="Beds" width="70px">
                <select value={form.bedrooms ?? ""} onChange={(e) => update("bedrooms", e.target.value === "" ? null : parseFloat(e.target.value))} style={inputStyle}>
                  {BEDROOM_OPTIONS.map((b) => <option key={String(b)} value={b ?? ""}>{b === null ? "-" : b === 0 ? "Studio" : b}</option>)}
                </select>
              </Field>
              <Field label="Baths" width="70px">
                <select value={form.bathrooms ?? ""} onChange={(e) => update("bathrooms", e.target.value === "" ? null : parseFloat(e.target.value))} style={inputStyle}>
                  {BATHROOM_OPTIONS.map((b) => <option key={String(b)} value={b ?? ""}>{b === null ? "-" : b}</option>)}
                </select>
              </Field>
              <Field label="Line" width="60px">
                <input type="text" value={form.floor_line || ""} onChange={(e) => update("floor_line", e.target.value)} style={inputStyle} />
              </Field>
              {!form.floor_line && listing.ai_floor_line && (
                <Field label="Est. Line" width="60px">
                  <input type="text" value={listing.ai_floor_line} disabled style={{ ...inputStyle, backgroundColor: "#f5f5f5", color: "#999" }} />
                </Field>
              )}
              <Field label="Status" width="140px">
                <select value={form.listing_status || ""} onChange={(e) => update("listing_status", e.target.value)} style={inputStyle}>
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              {isSale && (
                <Field label="Parking" width="80px">
                  <label style={{ display: "flex", alignItems: "center", gap: "4px", height: "32px", cursor: "pointer" }}>
                    <input type="checkbox" checked={form.deeded_parking || false} onChange={(e) => update("deeded_parking", e.target.checked)} style={{ width: "13px", height: "13px" }} />
                    <span style={{ fontSize: "11px", color: form.deeded_parking ? "#155724" : "#999" }}>Deeded</span>
                  </label>
                </Field>
              )}
            </FieldRow>
            <FieldRow style={{ marginTop: "6px" }}>
              <Field label="Listing URL" width="100%">
                <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                  <input type="text" value={form.listing_url || ""} onChange={(e) => update("listing_url", e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder="https://..." />
                  {form.listing_url && (
                    <a href={form.listing_url} target="_blank" rel="noopener noreferrer" title="Open listing" style={{ fontSize: "16px", textDecoration: "none", flexShrink: 0 }}>🔗</a>
                  )}
                </div>
              </Field>
            </FieldRow>
          </Section>

          {/* 2. Square Footage */}
          <Section title="Square Footage" sectionKey="sqft" isChecked={checked.sqft} onToggle={() => toggleChecked("sqft")}>
            <FieldRow>
              <Field label="Verified SqFt" width="100px">
                <input type="number" value={form.sqft_verified || ""} onChange={(e) => { update("sqft_verified", e.target.value); if (e.target.value) update("sqft", ""); }} style={inputStyle} />
              </Field>
              <Field label="Source" width="130px">
                <select value={form.sqft_verified_source || ""} onChange={(e) => update("sqft_verified_source", e.target.value)} style={inputStyle}>
                  {SQFT_SOURCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
              {form.sqft_verified_source === "other" && (
                <Field label="Specify" width="120px">
                  <input type="text" value={form.sqft_verified_source_other || ""} onChange={(e) => update("sqft_verified_source_other", e.target.value)} style={inputStyle} placeholder="Source..." />
                </Field>
              )}
              {!form.sqft_verified && (
                <Field label="Estimated SqFt" width="100px">
                  <input type="number" value={form.sqft ?? ""} onChange={(e) => { update("sqft", e.target.value === "" ? null : e.target.value); if (e.target.value) update("sqft_verified", ""); }} style={inputStyle} />
                </Field>
              )}
            </FieldRow>
            <FieldRow style={{ marginTop: "6px" }}>
              <Field label="Exterior SqFt" width="100px">
                <input type="number" value={form.sqft_exterior || ""} onChange={(e) => update("sqft_exterior", e.target.value)} style={inputStyle} />
              </Field>
              <Field label="Ext. Source" width="130px">
                <select value={form.sqft_exterior_source || ""} onChange={(e) => update("sqft_exterior_source", e.target.value)} style={inputStyle}>
                  {SQFT_SOURCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
              {form.sqft_exterior_source === "other" && (
                <Field label="Specify" width="120px">
                  <input type="text" value={form.sqft_exterior_source_other || ""} onChange={(e) => update("sqft_exterior_source_other", e.target.value)} style={inputStyle} placeholder="Source..." />
                </Field>
              )}
              {compositeSquareFeet && (
                <Field label="Composite" width="90px">
                  <div style={{ ...inputStyle, backgroundColor: "#f5f5f5", color: "#666", fontWeight: 600 }}>
                    {compositeSquareFeet.toLocaleString()}
                  </div>
                </Field>
              )}
            </FieldRow>
          </Section>

          {/* 3. Financial */}
          <Section title="Financial" sectionKey="financial" isChecked={checked.financial} onToggle={() => toggleChecked("financial")}>
            <FieldRow>
              <Field label="Price" width="110px">
                <CurrencyInput value={form.list_price} onChange={(v) => update("list_price", v === "" ? null : v)} style={inputStyle} />
              </Field>
              {!isSale && (
                <>
                  <Field label="Net Eff." width="100px">
                    <CurrencyInput value={form.net_effective_rent} onChange={(v) => update("net_effective_rent", v)} style={inputStyle} placeholder="Net eff." />
                  </Field>
                  <Field label="Mo. Free" width="60px">
                    <input type="number" value={form.months_free} onChange={(e) => update("months_free", e.target.value)} style={inputStyle} placeholder="0" />
                  </Field>
                  <Field label="Lease Mo." width="60px">
                    <input type="number" value={form.lease_term_months} onChange={(e) => update("lease_term_months", e.target.value)} style={inputStyle} placeholder="12" />
                  </Field>
                </>
              )}
              {isSale && (
                <>
                  <Field label="HOA" width="90px">
                    <CurrencyInput value={form.hoa} onChange={(v) => update("hoa", v === "" ? null : v)} style={inputStyle} />
                  </Field>
                  <Field label="Tax" width="90px">
                    <CurrencyInput value={form.tax} onChange={(v) => update("tax", v === "" ? null : v)} style={inputStyle} />
                  </Field>
                  <Field label="Taxes" width="90px">
                    <div style={{ display: "flex", alignItems: "center", height: "32px" }}>
                      {listing.listing_details?.tax_bill?.status === "no_data_verified" || form.tax_no_data ? (
                        <span
                          style={{ fontSize: "11px", color: "#d32f2f", fontWeight: 700, backgroundColor: "#ffebee", padding: "2px 6px", borderRadius: "4px", cursor: "pointer" }}
                          title="Click to undo"
                          onClick={() => update("tax_no_data", false)}
                        >
                          No DOF Data
                        </span>
                      ) : form.acris_url ? (
                        <span style={{ fontSize: "11px", color: "#2e7d32", fontWeight: 600 }}>Confirmed</span>
                      ) : (
                        <span
                          style={{ fontSize: "11px", color: "#e65100", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
                          title="Mark as no tax data on DOF"
                          onClick={() => update("tax_no_data", true)}
                        >
                          No Data?
                        </span>
                      )}
                    </div>
                  </Field>
                </>
              )}
            </FieldRow>
            {isSale && (
              <>
                <FieldRow style={{ marginTop: "8px" }}>
                  <Field label="" width="140px">
                    <label style={{ display: "flex", alignItems: "center", gap: "6px", height: "32px", cursor: "pointer" }}>
                      <input type="checkbox" checked={form.tax_abatement || false} onChange={(e) => update("tax_abatement", e.target.checked)} />
                      <span style={{ fontSize: "12px", fontWeight: 600, color: form.tax_abatement ? "#2e7d32" : "#666" }}>Tax Abatement</span>
                    </label>
                  </Field>
                  {form.tax_abatement && (
                    <>
                      <Field label="Type" width="140px">
                        <select value={form.tax_abatement_type || ""} onChange={(e) => update("tax_abatement_type", e.target.value)} style={inputStyle}>
                          <option value="">-- Select --</option>
                          <option value="421a">421a</option>
                          <option value="j-51">J-51</option>
                          <option value="condo_abatement">Condo Abatement</option>
                          <option value="other">Other</option>
                        </select>
                      </Field>
                      {form.tax_abatement_type === "other" && (
                        <Field label="Other Type" width="120px">
                          <input type="text" value={form.tax_abatement_type_other || ""} onChange={(e) => update("tax_abatement_type_other", e.target.value)} placeholder="Specify..." style={inputStyle} />
                        </Field>
                      )}
                      <Field label="Expires (Year)" width="100px">
                        <input type="number" value={form.tax_abatement_verified_date ? form.tax_abatement_verified_date.slice(0, 4) : ""} onChange={(e) => update("tax_abatement_verified_date", e.target.value)} placeholder="e.g. 2034" min="2020" max="2060" style={inputStyle} />
                      </Field>
                    </>
                  )}
                </FieldRow>
                {form.tax_abatement && (
                  <FieldRow style={{ marginTop: "4px" }}>
                    <Field label="Taxes w/ Abatement" width="140px">
                      <CurrencyInput allowDecimals value={form.tax_abatement_with} onChange={(v) => update("tax_abatement_with", v)} placeholder="$/mo" style={inputStyle} />
                    </Field>
                    <Field label="Taxes w/o Abatement" width="140px">
                      <CurrencyInput allowDecimals value={form.tax_abatement_without} onChange={(v) => update("tax_abatement_without", v)} placeholder="$/mo" style={inputStyle} />
                    </Field>
                  </FieldRow>
                )}
                <FieldRow style={{ marginTop: "6px" }}>
                  <Field label="ACRIS URL" width="100%">
                    <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                      <input type="text" value={form.acris_url || ""} onChange={(e) => update("acris_url", e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder="https://..." />
                      {form.acris_url && (
                        <a href={form.acris_url} target="_blank" rel="noopener noreferrer" title="Open ACRIS" style={{ fontSize: "16px", textDecoration: "none", flexShrink: 0 }}>🔗</a>
                      )}
                    </div>
                  </Field>
                </FieldRow>
                <FieldRow style={{ marginTop: "6px" }}>
                  <Field label="Borough" width="80px">
                    <input type="text" value={form.borough || ""} onChange={(e) => update("borough", e.target.value)} style={inputStyle} />
                  </Field>
                  <Field label="Block" width="80px">
                    <input type="text" value={form.block || ""} onChange={(e) => update("block", e.target.value)} style={inputStyle} />
                  </Field>
                  <Field label="Lot" width="80px">
                    <input type="text" value={form.lot || ""} onChange={(e) => update("lot", e.target.value)} style={inputStyle} />
                  </Field>
                  <a href="https://a836-pts-access.nyc.gov/care/search/commonsearch.aspx?mode=persprop" target="_blank" rel="noopener noreferrer" title="ACRIS Property Tax Lookup" style={{ fontSize: "13px", marginLeft: "4px", alignSelf: "center" }}>🔗</a>
                </FieldRow>
              </>
            )}
            {listing.predicted_rent && (
              <div style={{ marginTop: "6px", fontSize: "12px", color: "#666" }}>
                ML Rent: <b>${listing.predicted_rent?.toLocaleString()}</b>
                {listing.cap_rate != null && <> &bull; Cap Rate: <b>{listing.cap_rate}%</b></>}
              </div>
            )}
          </Section>

          {/* 4. Listing Date & History */}
          <Section title="Listing Date & History" sectionKey="history" isChecked={checked.history} onToggle={() => toggleChecked("history")}>
            <FieldRow>
              <Field label="Date Listed" width="140px">
                <input type="date" value={form.created_date || ""} onChange={(e) => update("created_date", e.target.value)} style={inputStyle} />
              </Field>
              <Field label="Closing Date" width="140px">
                <input type="date" value={form.closing_date || ""} onChange={(e) => update("closing_date", e.target.value)} style={inputStyle} />
              </Field>
              <Field label="Days on Market" width="90px">
                <input
                  type="number"
                  value={form.active_dom ?? daysOnMarket ?? ""}
                  onChange={(e) => setForm({ ...form, active_dom: e.target.value ? parseInt(e.target.value) : null })}
                  style={{ ...inputStyle, color: (form.active_dom ?? daysOnMarket) > 90 ? "#c62828" : (form.active_dom ?? daysOnMarket) > 30 ? "#e65100" : "#2e7d32", fontWeight: 600 }}
                />
              </Field>
              <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: "2px" }}>
                <button onClick={showHistory ? () => setShowHistory(false) : loadHistory} style={{ ...smallBtnStyle, backgroundColor: "#f5f5f5" }}>
                  {showHistory ? "Hide History" : "Show History"}
                </button>
              </div>
            </FieldRow>
            {showHistory && (
              <div style={{ marginTop: "8px" }}>
                {history.length === 0 ? (
                  <div style={{ fontSize: "12px", color: "#999", padding: "4px 0" }}>No history records</div>
                ) : (
                  <div style={{ maxHeight: "160px", overflowY: "auto", border: "1px solid #eee", borderRadius: "4px" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                      <thead>
                        <tr style={{ backgroundColor: "#fafafa" }}>
                          <th style={histThStyle}>Date</th>
                          <th style={histThStyle}>Price</th>
                          <th style={histThStyle}>Event</th>
                          <th style={histThStyle}>Status</th>
                          <th style={{ ...histThStyle, width: "30px" }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map((h) => (
                          <tr key={h.id}>
                            <td style={histTdStyle}>{h.changed_date ? new Date(h.changed_date).toLocaleDateString() : "-"}</td>
                            <td style={histTdStyle}>{h.list_price ? `$${h.list_price.toLocaleString()}` : "-"}</td>
                            <td style={{ ...histTdStyle, fontSize: "10px", color: "#888" }}>{h.event_name || "-"}</td>
                            <td style={histTdStyle}>{h.listing_status || "-"}</td>
                            <td style={histTdStyle}>
                              <span
                                style={{ color: "#c62828", cursor: "pointer", fontSize: "11px" }}
                                title="Delete"
                                onClick={async () => {
                                  if (!window.confirm("Delete this history entry?")) return;
                                  try {
                                    await authFetch(`/listings/${listing.id}/history/${h.id}`, { method: "DELETE" });
                                    loadHistory();
                                  } catch (err) { console.error("Delete failed:", err); }
                                }}
                              >✕</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {showAddHistory ? (
                  <div style={{ marginTop: "6px", display: "flex", gap: "6px", alignItems: "flex-end" }}>
                    <Field label="Date" width="120px">
                      <input type="date" value={newHistory.changed_date} onChange={(e) => setNewHistory((h) => ({ ...h, changed_date: e.target.value }))} style={{ ...inputStyle, fontSize: "11px" }} />
                    </Field>
                    <Field label="Price" width="100px">
                      <CurrencyInput value={newHistory.list_price} onChange={(v) => setNewHistory((h) => ({ ...h, list_price: v }))} style={{ ...inputStyle, fontSize: "11px" }} />
                    </Field>
                    <Field label="Event" width="120px">
                      <input type="text" value={newHistory.event_name} onChange={(e) => setNewHistory((h) => ({ ...h, event_name: e.target.value }))} placeholder="e.g. Listed for sale" style={{ ...inputStyle, fontSize: "11px" }} />
                    </Field>
                    <Field label="Status" width="120px">
                      <select value={newHistory.listing_status} onChange={(e) => setNewHistory((h) => ({ ...h, listing_status: e.target.value }))} style={{ ...inputStyle, fontSize: "11px" }}>
                        <option value="">-</option>
                        {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </Field>
                    <button onClick={addHistoryEntry} disabled={addingHistory || !newHistory.changed_date} style={{ ...smallBtnStyle, backgroundColor: "#e8f5e9", color: "#2e7d32", marginBottom: "0" }}>
                      {addingHistory ? "..." : "Add"}
                    </button>
                    <button onClick={() => setShowAddHistory(false)} style={{ ...smallBtnStyle, marginBottom: "0" }}>Cancel</button>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
                    <button onClick={() => setShowAddHistory(true)} style={{ ...smallBtnStyle, fontSize: "11px" }}>+ Add Entry</button>
                    <button onClick={() => { setShowSEPaste(true); setSeParsed(null); setSePasteText(""); }} style={{ ...smallBtnStyle, fontSize: "11px", backgroundColor: "#e3f2fd", color: "#1565c0" }}>Paste from SE</button>
                  </div>
                )}
                {showSEPaste && (
                  <div style={{ marginTop: "8px", border: "1px solid #bbdefb", borderRadius: "6px", padding: "10px", backgroundColor: "#f5f9ff" }}>
                    {!seParsed ? (
                      <>
                        <div style={{ fontSize: "11px", color: "#666", marginBottom: "4px" }}>Paste StreetEasy price history below:</div>
                        <textarea
                          value={sePasteText}
                          onChange={(e) => setSePasteText(e.target.value)}
                          style={{ ...inputStyle, width: "100%", minHeight: "80px", resize: "vertical", fontFamily: "inherit", fontSize: "11px", boxSizing: "border-box" }}
                          placeholder={"Date\tPrice\tEvent\n3/3/2026\n$1,850,000\nListed by..."}
                        />
                        <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
                          <button onClick={handleSEParse} disabled={!sePasteText.trim()} style={{ ...smallBtnStyle, backgroundColor: "#e3f2fd", color: "#1565c0" }}>Parse</button>
                          <button onClick={() => setShowSEPaste(false)} style={smallBtnStyle}>Cancel</button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: "11px", color: "#666", marginBottom: "4px" }}>
                          {seParsed.filter((e) => e.include).length} of {seParsed.length} entries selected
                          {seParsed.some((e) => e.isDupe) && (
                            <span style={{ color: "#e65100", marginLeft: "8px" }}>
                              ({seParsed.filter((e) => e.isDupe).length} dupes auto-unchecked)
                            </span>
                          )}
                        </div>
                        <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: "4px" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }}>
                            <thead>
                              <tr style={{ backgroundColor: "#fafafa" }}>
                                <th style={{ ...histThStyle, width: "30px" }}></th>
                                <th style={histThStyle}>Date</th>
                                <th style={histThStyle}>Price</th>
                                <th style={histThStyle}>Event</th>
                                <th style={histThStyle}>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {seParsed.map((e, i) => (
                                <tr key={i} style={{ opacity: e.include ? 1 : 0.4, backgroundColor: e.isDupe ? "#fff3e0" : "transparent" }}>
                                  <td style={histTdStyle}>
                                    <input type="checkbox" checked={e.include} onChange={() => toggleSEEntry(i)} />
                                  </td>
                                  <td style={histTdStyle}>{new Date(e.changed_date + "T00:00:00").toLocaleDateString()}</td>
                                  <td style={histTdStyle}>{e.list_price ? `$${e.list_price.toLocaleString()}` : "-"}</td>
                                  <td style={{ ...histTdStyle, fontSize: "10px", color: "#888" }}>{e.event}</td>
                                  <td style={histTdStyle}>{e.listing_status || "-"}{e.isDupe && <span style={{ color: "#e65100", fontSize: "9px", marginLeft: "4px" }}>DUPE</span>}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
                          <button onClick={addSEEntries} disabled={seAdding || seParsed.filter((e) => e.include).length === 0} style={{ ...smallBtnStyle, backgroundColor: "#e8f5e9", color: "#2e7d32" }}>
                            {seAdding ? "Adding..." : `Add ${seParsed.filter((e) => e.include).length} Entries`}
                          </button>
                          <button onClick={() => setSeParsed(null)} style={smallBtnStyle}>Back</button>
                          <button onClick={() => setShowSEPaste(false)} style={smallBtnStyle}>Cancel</button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </Section>

          {/* 5. Media */}
          <Section title="Media" sectionKey="media" isChecked={checked.media} onToggle={() => toggleChecked("media")}>
            <FieldRow>
              <Field label="Floor Plan" width="120px">
                <select value={form.floorplan_status || ""} onChange={(e) => update("floorplan_status", e.target.value)} style={inputStyle}>
                  {MEDIA_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
              {(form.floorplan_status === "available" || form.floorplan_link) && (
                <Field label="Floor Plan URL" width="calc(50% - 72px)">
                  <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                    <input type="text" value={form.floorplan_link || ""} onChange={(e) => update("floorplan_link", e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder="https://..." />
                    {form.floorplan_link && (
                      <a href={form.floorplan_link} target="_blank" rel="noopener noreferrer" title="Open floor plan" style={{ fontSize: "16px", textDecoration: "none", flexShrink: 0 }}>🔗</a>
                    )}
                  </div>
                </Field>
              )}
              <Field label="Floor Plan Notes" width={form.floorplan_status === "available" || form.floorplan_link ? "calc(50% - 72px)" : "calc(100% - 132px)"}>
                <input type="text" value={form.floorplan_notes || ""} onChange={(e) => update("floorplan_notes", e.target.value)} style={inputStyle} placeholder="Notes..." />
              </Field>
            </FieldRow>
            <FieldRow style={{ marginTop: "6px" }}>
              <Field label="Video" width="120px">
                <select value={form.video_status || ""} onChange={(e) => update("video_status", e.target.value)} style={inputStyle}>
                  {MEDIA_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
              {(form.video_status === "available" || form.video_link) && (
                <Field label="Video URL" width="calc(50% - 72px)">
                  <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                    <input type="text" value={form.video_link || ""} onChange={(e) => update("video_link", e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder="https://..." />
                    {form.video_link && (
                      <a href={form.video_link} target="_blank" rel="noopener noreferrer" title="Open video" style={{ fontSize: "16px", textDecoration: "none", flexShrink: 0 }}>🔗</a>
                    )}
                  </div>
                </Field>
              )}
              <Field label="Video Notes" width={form.video_status === "available" || form.video_link ? "calc(50% - 72px)" : "calc(100% - 132px)"}>
                <input type="text" value={form.video_notes || ""} onChange={(e) => update("video_notes", e.target.value)} style={inputStyle} placeholder="Notes..." />
              </Field>
            </FieldRow>
          </Section>

          {/* 6. Amenities */}
          <Section title="Amenities" sectionKey="amenities" isChecked={checked.amenities} onToggle={() => toggleChecked("amenities")}>
            <FieldRow>
              <Field label="Dishwasher" width="90px">
                <select
                  value={form.dishwasher === null || form.dishwasher === undefined ? "" : form.dishwasher === true ? "true" : "false"}
                  onChange={(e) => update("dishwasher", e.target.value === "" ? null : e.target.value === "true")}
                  style={inputStyle}
                >
                  <option value="">-</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </Field>
              <Field label="W/D" width="90px">
                <select value={form.washer_dryer ?? ""} onChange={(e) => update("washer_dryer", e.target.value || null)} style={inputStyle}>
                  {WASHER_DRYER_OPTIONS.map((o) => <option key={String(o.value)} value={o.value ?? ""}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Reno Level" width="110px">
                <select value={form.renovation_level ?? ""} onChange={(e) => update("renovation_level", e.target.value || null)} style={inputStyle}>
                  {RENOVATION_OPTIONS.map((o) => <option key={String(o.value)} value={o.value ?? ""}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Premium Views" width="100px">
                <select value={form.premium_views ?? ""} onChange={(e) => update("premium_views", e.target.value || null)} style={inputStyle}>
                  {VIEWS_OPTIONS.map((o) => <option key={String(o.value)} value={o.value ?? ""}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Outdoor" width="120px">
                <select
                  value={form.outdoor_type || ""}
                  onChange={(e) => update("outdoor_type", e.target.value)}
                  style={inputStyle}
                >
                  {OUTDOOR_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </Field>
              {!isSale && (
                <Field label="Furnished" width="80px">
                  <select
                    value={form.furnished ? "true" : "false"}
                    onChange={(e) => update("furnished", e.target.value === "true")}
                    style={inputStyle}
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </Field>
              )}
            </FieldRow>
          </Section>

          {/* 7. Comments */}
          <Section title="Comments" sectionKey="comments" isChecked={checked.comments} onToggle={() => toggleChecked("comments")}>
            <Field label="" width="100%">
              <textarea
                value={form.comments || ""}
                onChange={(e) => update("comments", e.target.value)}
                style={{ ...inputStyle, minHeight: "60px", resize: "vertical", fontFamily: "inherit" }}
                placeholder="Add notes or comments about this listing..."
                rows={3}
              />
            </Field>
          </Section>
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <button onClick={onClose} style={cancelBtnStyle}>Cancel</button>
          <div style={{ display: "flex", gap: "8px" }}>
            <button onClick={handleSave} disabled={saving || verifying} style={saveBtnStyle}>
              {saving ? "Saving..." : "Save"}
            </button>
            <button onClick={handleVerify} disabled={saving || verifying} style={verifyBtnStyle}>
              {verifying ? "Verifying..." : "Save & Verify"}
            </button>
          </div>
        </div>
      </div>

      {showDebug && (
        <ListingDebugModal listingId={listing.id} onClose={() => setShowDebug(false)} />
      )}

      {showTypeChange && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 }} onClick={() => setShowTypeChange(false)}>
          <div style={{ background: "#fff", borderRadius: 8, padding: 22, width: 480, boxShadow: "0 8px 30px rgba(0,0,0,0.3)" }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 10px", fontSize: 16, color: "#c62828" }}>&#9888; Change listing type?</h3>
            <p style={{ fontSize: 13, color: "#333", margin: "0 0 10px" }}>
              Switch from <b>{isSale ? "Sale" : "Rental"}</b> to <b>{isSale ? "Rental" : "Sale"}</b>. Only do this to fix a mislabeled listing. It will:
            </p>
            <ul style={{ fontSize: 12.5, color: "#444", margin: "0 0 14px", paddingLeft: 18, lineHeight: 1.55 }}>
              <li><b>Keep the price as-is</b>, but its meaning flips ({isSale ? "sale price → monthly rent" : "monthly rent → sale price"}) — <b>re-enter the correct price after</b>.</li>
              <li>Clear this listing's cached <b>predictions &amp; comps</b> ({isSale ? "rentals don't get predictions" : "it'll re-predict as a sale"}).</li>
              <li>Send it back to <b>To Review</b> (verification rules differ by type).</li>
              <li>Be <b>recorded</b> (who &amp; when) in the listing's history.</li>
            </ul>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setShowTypeChange(false)} style={{ padding: "8px 16px", border: "1px solid #ccc", borderRadius: 6, background: "#fff", cursor: "pointer", fontSize: 13 }}>Cancel</button>
              <button onClick={handleChangeType} disabled={changingType} style={{ padding: "8px 18px", border: "none", borderRadius: 6, background: "#c62828", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                {changingType ? "Changing…" : `Yes, make it a ${isSale ? "Rental" : "Sale"}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Sub-components ---

function Section({ title, sectionKey, isChecked, onToggle, children }) {
  return (
    <div style={{ marginBottom: "16px" }}>
      <div style={{ fontSize: "11px", fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "8px", borderBottom: "1px solid #eee", paddingBottom: "4px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>{title}</span>
        {sectionKey && (
          <label style={{ display: "flex", alignItems: "center", gap: "4px", cursor: "pointer", textTransform: "none" }}>
            <input type="checkbox" checked={!!isChecked} onChange={onToggle} style={{ cursor: "pointer" }} />
            <span style={{ fontSize: "10px", fontWeight: 500, color: isChecked ? "#2e7d32" : "#aaa" }}>{isChecked ? "Done" : "Todo"}</span>
          </label>
        )}
      </div>
      {children}
    </div>
  );
}

function FieldRow({ children, style }) {
  return <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", ...style }}>{children}</div>;
}

function Field({ label, width, children }) {
  return (
    <div style={{ width, minWidth: 0 }}>
      <div style={{ fontSize: "11px", color: "#666", marginBottom: "3px" }}>{label}</div>
      {children}
    </div>
  );
}

// --- Styles ---

const overlayStyle = {
  position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: "rgba(0,0,0,0.4)", zIndex: 10000,
  display: "flex", alignItems: "center", justifyContent: "center",
};

const modalStyle = {
  backgroundColor: "#fff", borderRadius: "12px", width: "720px", maxHeight: "90vh",
  display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
};

const headerStyle = {
  display: "flex", justifyContent: "space-between", alignItems: "flex-start",
  padding: "20px 24px 16px", borderBottom: "1px solid #eee",
};

const closeBtnStyle = {
  background: "none", border: "none", fontSize: "24px", cursor: "pointer",
  color: "#999", padding: "0 4px", lineHeight: 1,
};

const bodyStyle = {
  padding: "20px 24px", overflowY: "auto", flex: 1,
};

const footerStyle = {
  display: "flex", justifyContent: "space-between", alignItems: "center",
  padding: "16px 24px", borderTop: "1px solid #eee",
};

const inputStyle = {
  width: "100%", padding: "6px 8px", border: "1px solid #ddd", borderRadius: "4px",
  fontSize: "13px", boxSizing: "border-box",
};

const smallBtnStyle = {
  padding: "4px 10px", border: "1px solid #ccc", borderRadius: "4px",
  backgroundColor: "#fff", cursor: "pointer", fontSize: "12px",
};

const cancelBtnStyle = {
  padding: "8px 16px", border: "1px solid #ccc", borderRadius: "6px",
  backgroundColor: "#fff", cursor: "pointer", fontSize: "13px",
};

const saveBtnStyle = {
  padding: "8px 20px", border: "1px solid #1976d2", borderRadius: "6px",
  backgroundColor: "#e3f2fd", color: "#1565c0", cursor: "pointer",
  fontSize: "13px", fontWeight: 600,
};

const verifyBtnStyle = {
  padding: "8px 20px", border: "1px solid #2e7d32", borderRadius: "6px",
  backgroundColor: "#e8f5e9", color: "#2e7d32", cursor: "pointer",
  fontSize: "13px", fontWeight: 600,
};

const autoVerifyBtnStyle = {
  padding: "6px 14px", border: "1px solid #1565c0", borderRadius: "6px",
  backgroundColor: "#1565c0", color: "#fff", cursor: "pointer",
  fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap",
};

const histThStyle = {
  padding: "4px 8px", textAlign: "left", fontWeight: 600, borderBottom: "1px solid #eee",
};

const histTdStyle = {
  padding: "4px 8px", borderBottom: "1px solid #f5f5f5",
};
